import { FC, useRef, useState } from "react";

import { BoxProps, Column } from "@hightouch/htui";

import { WorkspaceSelector } from "src/components/layout/nav/workspace-selector";
import { useNavWidth } from "src/contexts/nav-width-context";
import { useIsAgenticSurfaceActive } from "src/hooks/use-agentic-home-enabled";

import { Footer } from "./footer";
import { NavHeader } from "./header";
import { Activate } from "./sections/activate";
import { Agents } from "./sections/agents";
import { CustomerStudio } from "./sections/customer-studio";
import { DecisionEngines } from "./sections/decision-engine";
import { EventCollection } from "./sections/event-collection";
import { GetStarted } from "./sections/get-started";
import { Home } from "./sections/home";
import { IdentityResolution } from "./sections/identity-resolution";
import { Integrations } from "./sections/integrations";
import { Intelligence } from "./sections/intelligence";
import { useActiveSection } from "./utils";
import { LifecycleStudio } from "./sections/lifecycle-studio";

import { AdStudio } from "./sections/ad-studio";
import { ContextHub } from "./sections/context-hub";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import { CEP } from "./sections/cep";

import { SpaceSelector, useSpaceContext } from "src/components/spaces";

export const Nav: FC = () => {
  const { canNavOpen, isNavOpen, setIsNavOpen } = useNavWidth();
  const [isCollapsedNavHovered, setIsCollapsedNavHovered] = useState(false);
  const {
    isSpacesEnabled,
    selectedSpace,
    availableSpaces,
    isUserSpaceScoped,
    selectSpace,
  } = useSpaceContext();

  function handleDeadSpaceClick(e: React.MouseEvent<HTMLDivElement>) {
    // Only trigger on dead space (not on nav items/sections)
    if (e.target === e.currentTarget && !isNavOpen && canNavOpen) {
      setIsNavOpen(true);
    }
  }

  return (
    <NavContainer
      isNavOpen={isNavOpen}
      onClick={handleDeadSpaceClick}
      onCollapsedHoverChange={setIsCollapsedNavHovered}
    >
      <Column px={2} gap={4}>
        <NavHeader showToggleHoverState={!isNavOpen && isCollapsedNavHovered} />
        <Column gap={2}>
          <WorkspaceSelector />
          {isSpacesEnabled && availableSpaces.length > 0 && (
            <SpaceSelector
              selectedSpace={selectedSpace}
              availableSpaces={availableSpaces}
              isUserSpaceScoped={isUserSpaceScoped}
              isNavOpen={isNavOpen}
              selectSpace={selectSpace}
            />
          )}
        </Column>
      </Column>
      <NavSections onClick={handleDeadSpaceClick} />
    </NavContainer>
  );
};

type NavContainerProps = Pick<BoxProps, "children" | "onClick"> & {
  isNavOpen: boolean;
  onCollapsedHoverChange: (hovered: boolean) => void;
};

const NavContainer = ({
  isNavOpen,
  children,
  onClick,
  onCollapsedHoverChange,
}: NavContainerProps) => {
  const { navWidth } = useNavWidth();
  const navRef = useRef<HTMLDivElement>(null);
  const hasTransparentBackground = useIsAgenticSurfaceActive();

  return (
    <Column
      ref={navRef}
      as="aside"
      cursor={{ base: "default", lg: isNavOpen ? "default" : "e-resize" }}
      background={hasTransparentBackground ? "transparent" : "base.background"}
      display="flex"
      flexDir="column"
      gap={2}
      height="100vh"
      overflow="hidden"
      position="sticky"
      top={0}
      width={navWidth}
      maxWidth={navWidth}
      py={4}
      pl={0.5}
      zIndex={2}
      onClick={onClick}
      onMouseEnter={() => !isNavOpen && onCollapsedHoverChange(true)}
      onMouseLeave={() => onCollapsedHoverChange(false)}
    >
      {children}
    </Column>
  );
};

const NavSections = ({ onClick }: Pick<BoxProps, "onClick">) => {
  const activeState = useActiveSection();
  const { isNavOpen } = useNavWidth();
  const scrollFade = useScrollFade();

  return (
    <Column
      as="nav"
      aria-label="Main navigation"
      flex={1}
      gap={2}
      justifyContent="space-between"
      minHeight={0}
      width="100%"
      onClick={onClick}
    >
      <Column
        ref={scrollFade.ref}
        cursor="default"
        gap={3}
        overflowY="auto"
        py={2}
        sx={{
          "& > *": { mx: 2 },
          ...scrollFade.sx,
          // Hide scrollbar when collapsed to prevent icon alignment issues
          ...(!isNavOpen && {
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }),
        }}
      >
        <GetStarted />
        <Home />
        <Agents {...activeState} />
        <Activate {...activeState} />
        <CustomerStudio {...activeState} />
        <LifecycleStudio {...activeState} />
        <AdStudio {...activeState} />
        <ContextHub {...activeState} />
        <DecisionEngines {...activeState} />
        <CEP {...activeState} />
        <Intelligence {...activeState} />
        <IdentityResolution />
        <Integrations {...activeState} />
        <EventCollection {...activeState} />
      </Column>
      <Footer {...activeState} />
    </Column>
  );
};
